// src/support/harborChartRuntimeUtils.ts

export type HarborChartKind = 'bar' | 'line' | 'pie' | 'donut' | 'gauge' | 'unknown';

export interface HarborColorRuntimeSettings {
  useCategorical?: boolean;   // default true (Magnetic UX)
  colorScheme?: string;       // e.g. 'nivo'
  colors?: string[];          // explicit palette
  seriesColors?: Record<string, string>; // optional per-series override (key -> color)
  colorBy?: 'id' | 'indexValue';
}

export interface HarborAxesRuntimeSettings {
  xAxisLabel?: string;
  yAxisLabel?: string;
  axisBottomLegend?: string;
  axisLeftLegend?: string;
  axisBottomTickValues?: Array<string | number>;
  axisLeftTickValues?: Array<string | number>;
}

export interface HarborGridRuntimeSettings {
  enableGridX?: boolean;
  enableGridY?: boolean;
  gridXValues?: Array<string | number>;
  gridYValues?: Array<string | number>;
}

export interface ApplyRuntimeOptions {
  kind: HarborChartKind;
  keys?: string[]; // series keys (for bar/line)
  defaultColorBy?: 'id' | 'indexValue';
  /**
   * optional post-merge util function name to call if it exists.
   * Harbor bar exposes util.postUpdateMergedBarConfig.
   * Other charts may expose similar in future; safe to leave undefined.
   */
  postMergeUtilFnName?: string; // e.g. 'postUpdateMergedBarConfig'
}

/**
 * Decide if the user intends to override Magnetic defaults.
 * Rule (recommended):
 * - explicit toggle OR explicit palette OR explicit scheme.
 *
 * If you want scheme selection alone NOT to imply override, remove the scheme check.
 */
export function shouldUseCustomColorsRuntime(s: HarborColorRuntimeSettings): boolean {
  const useCategorical = s.useCategorical !== false;

  const explicitPalette = Array.isArray(s.colors) && s.colors.length > 0;
  const explicitScheme = typeof s.colorScheme === 'string' && s.colorScheme.length > 0;
  const explicitSeriesColors = !!s.seriesColors && Object.keys(s.seriesColors).length > 0;

  // If Magnetic is OFF, always allow overrides.
  if (!useCategorical) return true;

  // If Magnetic is ON, only override when the user explicitly configured colors.
  return explicitPalette || explicitScheme || explicitSeriesColors;
}

/**
 * Apply colors override in modifyHbrConfig in a Harbor-safe way.
 * Mutates cfg and also returns it.
 */
export function applyHarborColorsInModifyConfig(
  cfg: any,
  settings: HarborColorRuntimeSettings,
  util: any,
  opts: ApplyRuntimeOptions
) {
  const useCategorical = settings.useCategorical !== false;
  const override = shouldUseCustomColorsRuntime(settings);
  if (!override) return cfg;

  const keys = Array.isArray(opts.keys) ? opts.keys : Array.isArray(cfg?.keys) ? cfg.keys : [];

  // Decide colorBy (bar/line generally wants id-based if keys are series)
  cfg.colorBy = settings.colorBy ?? opts.defaultColorBy ?? 'id';

  const explicitPalette = Array.isArray(settings.colors) ? settings.colors : [];
  const scheme = typeof settings.colorScheme === 'string' ? settings.colorScheme : undefined;

  // seriesColors -> exact key alignment if complete
  const seriesColors =
    settings.seriesColors && typeof settings.seriesColors === 'object' ? settings.seriesColors : undefined;

  if (seriesColors && keys.length > 0) {
    const mapped = keys.map((k) => seriesColors[k]).filter(Boolean);
    if (mapped.length === keys.length) {
      cfg.colors = mapped;
    } else if (explicitPalette.length > 0) {
      cfg.colors = explicitPalette;
    } else if (scheme) {
      cfg.colors = { scheme };
    }
  } else if (explicitPalette.length > 0) {
    cfg.colors = explicitPalette;
  } else if (scheme) {
    cfg.colors = { scheme };
  }

  // Optional: let Harbor re-merge config if util provides a post-merge function
  if (useCategorical && util && opts.postMergeUtilFnName) {
    const fn = util?.[opts.postMergeUtilFnName];
    if (typeof fn === 'function') {
      try {
        cfg = fn(cfg);
      } catch {
        // ignore
      }
    }
  }

  return cfg;
}

/**
 * Apply axis label/ticks in modifyHbrConfig.
 * Maps builder X/Y axis label fields into axis legends.
 */
export function applyHarborAxesInModifyConfig(cfg: any, s: HarborAxesRuntimeSettings) {
  const axisBottom = cfg.axisBottom ?? {};
  const axisLeft = cfg.axisLeft ?? {};

  const bottomLegend =
    (s.axisBottomLegend && s.axisBottomLegend.trim()) ||
    (s.xAxisLabel && s.xAxisLabel.trim()) ||
    axisBottom.legend;

  const leftLegend =
    (s.axisLeftLegend && s.axisLeftLegend.trim()) ||
    (s.yAxisLabel && s.yAxisLabel.trim()) ||
    axisLeft.legend;

  cfg.axisBottom = {
    ...axisBottom,
    ...(bottomLegend ? { legend: bottomLegend } : {}),
    ...(Array.isArray(s.axisBottomTickValues) ? { tickValues: s.axisBottomTickValues } : {}),
  };

  cfg.axisLeft = {
    ...axisLeft,
    ...(leftLegend ? { legend: leftLegend } : {}),
    ...(Array.isArray(s.axisLeftTickValues) ? { tickValues: s.axisLeftTickValues } : {}),
  };

  return cfg;
}

/**
 * Apply grid toggles + explicit grid values.
 */
export function applyHarborGridInModifyConfig(cfg: any, s: HarborGridRuntimeSettings) {
  cfg.enableGridX = !!s.enableGridX;
  cfg.enableGridY = s.enableGridY !== false;

  if (Array.isArray(s.gridXValues)) cfg.gridXValues = s.gridXValues;
  if (Array.isArray(s.gridYValues)) cfg.gridYValues = s.gridYValues;

  return cfg;
}

/**
 * Convenience: apply common runtime behavior across charts.
 */
export function applyCommonHarborRuntimeInModifyConfig(
  cfg: any,
  settings: {
    colors?: HarborColorRuntimeSettings;
    axes?: HarborAxesRuntimeSettings;
    grid?: HarborGridRuntimeSettings;
  },
  util: any,
  opts: ApplyRuntimeOptions
) {
  if (settings.grid) applyHarborGridInModifyConfig(cfg, settings.grid);
  if (settings.axes) applyHarborAxesInModifyConfig(cfg, settings.axes);
  if (settings.colors) cfg = applyHarborColorsInModifyConfig(cfg, settings.colors, util, opts);
  return cfg;
}